import type {
  FormattedDevice,
  NewPlayerSessionInput,
  RawTeamPersonSessionData
} from '@frontend/core'
import { isDeviceType } from '@frontend/core/lib/domains/hardware'

/**
 * Pure decision logic for the battery / offline device watcher.
 *
 * The diagnostics websocket pushes a full hardware snapshot on every frame and
 * the notification surface performs no de-duplication, so the edge-trigger
 * (fire once per entry into a bad state, re-arm on recovery) has to live here.
 * Keeping it React-free makes the transition logic unit-testable without
 * rendering, and keeps the component file free of non-component exports.
 *
 * Every device on the network (anchor, ball, player tag) is watched, and each
 * toast names what the device is. Battery fires on the rising edge once a
 * freshly-online device has settled (a boot-time `0` reading is ignored for the
 * first `ONLINE_DEBOUNCE_COUNT` snapshots); offline is debounced so a brief blip
 * of packet loss doesn't raise a false alert — a device must stay offline for
 * `OFFLINE_DEBOUNCE_COUNT` snapshots before it fires.
 *
 * The debounce/settling windows are counted in **diagnostics snapshots, not
 * wall-clock time.** The hardware/backend pipeline can lag, so the first one or
 * two snapshots after a device comes online may still carry stale battery and
 * status fields; counting wall-clock time let those stale frames trip a toast
 * (e.g. a false critical-battery toast stacked on the come-online toast).
 * Requiring a fixed number of *received* snapshots guarantees we've seen that
 * many real readings before we trust the state, regardless of stream cadence.
 */

/** Battery is "critical" strictly below this percentage. */
export const BATTERY_CRITICAL_THRESHOLD = 10

/**
 * A device must be reported offline in this many consecutive diagnostics
 * snapshots before an offline toast fires, so transient packet loss doesn't
 * trigger a false alert.
 */
export const OFFLINE_DEBOUNCE_COUNT = 5

/**
 * A device must be reported online in this many consecutive diagnostics
 * snapshots before a recovered device fires its "has come online" toast, and
 * before its battery reading is trusted — a freshly-online device can report a
 * stale `0` battery (or other wrong fields) for the first few messages, before
 * the hardware/backend pipeline catches up.
 */
export const ONLINE_DEBOUNCE_COUNT = 5

export type NotificationCondition = 'battery' | 'offline' | 'online'

/** Trailing phrase rendered after the device descriptor for each condition. */
export const CONDITION_LABELS: Record<NotificationCondition, string> = {
  battery: 'battery level critical',
  offline: 'has gone offline',
  online: 'has come online'
}

export type DeviceArmedState = {
  /** Already notified for the current battery-critical episode. */
  battery: boolean
  /** Already notified for the current offline episode. */
  offline: boolean
  /**
   * Come-online handled: `true` for every state except a device sitting in a
   * pending come-online recovery debounce. Set `true` once the toast has fired,
   * and also for baselines, joins, and offline devices (which are not online
   * and so never have a pending recovery) — so `online === false` uniquely
   * marks a device that is online but still inside its recovery debounce window.
   */
  online: boolean
  /**
   * Consecutive diagnostics snapshots the device has been offline, capped at
   * `OFFLINE_DEBOUNCE_COUNT`; `0` while online.
   */
  offlineCount: number
  /**
   * Consecutive diagnostics snapshots the device has been online, capped at
   * `ONLINE_DEBOUNCE_COUNT`; `0` while not online.
   */
  onlineCount: number
}

/** Armed-state map keyed by device id, held across renders on a ref. */
export type ArmedStateMap = Record<string, DeviceArmedState>

/** Resolved player identity for an assigned tag. */
export type Assignment = {
  name: string
  number: number
}

/** hardwareId → assigned player identity, for tags in the active session. */
export type AssignmentMap = Map<string, Assignment>

export type FiredNotification = {
  id: string
  condition: NotificationCondition
  message: string
}

/**
 * Human-readable label for what a device is. Returns null for unknown device
 * types, which is also how we drop them out of scope.
 */
export const getDeviceTypeLabel = (device: FormattedDevice) => {
  if (isDeviceType.ball(device.type)) return 'Ball'
  if (isDeviceType.anchor(device.type)) return 'Anchor'
  if (isDeviceType.playerTag(device.type)) return 'Tag'

  return null
}

/**
 * Build the hardwareId → { name, number } map from the active session's raw
 * players. Name is resolved from `teamPerson.person` (never keyed off
 * hardwareId for identity); guarded with optional chaining because child data
 * fields can be null.
 */
export const buildAssignmentMap = (
  players:
    | (RawTeamPersonSessionData | NewPlayerSessionInput)[]
    | undefined
    | null
) => {
  const map: AssignmentMap = new Map()
  if (!players) return map

  for (const player of players) {
    if (!player?.hardwareId) continue
    const firstName = player.teamPerson?.person?.firstName ?? ''
    const lastName = player.teamPerson?.person?.lastName ?? ''
    const name = `${firstName} ${lastName}`.trim()
    map.set(player.hardwareId, { name, number: player.number })
  }

  return map
}

/**
 * Scope gate: every anchor, ball, and player tag on the network is watched;
 * unknown device types are skipped.
 */
export const isInScope = (device: FormattedDevice) =>
  getDeviceTypeLabel(device) !== null

/**
 * Evaluate both bad-state conditions for a device.
 *
 * Battery is critical when the value is strictly below the threshold, the
 * device is not charging (any charging state other than `notCharging`
 * suppresses it), and the device is online — an offline device only reports its
 * last-known (stale) battery, so we never raise a battery alert for one; its
 * offline state is what matters. `sleep` keeps a valid reading, so it does not
 * suppress battery. Offline is purely `status === 'offline'`; `sleep` never
 * counts as offline.
 */
export const evaluateDevice = (device: FormattedDevice) => {
  const battery = device.metrics.battery.value
  const offline = device.metrics.status.value === 'offline'

  const batteryCritical =
    !offline &&
    typeof battery === 'number' &&
    battery < BATTERY_CRITICAL_THRESHOLD &&
    device.chargingState === 'notCharging'

  return { batteryCritical, offline }
}

/** The resolved descriptor pieces for a device, before any condition phrase. */
export type DeviceMessageParts = {
  /** What the device is: `'Ball' | 'Anchor' | 'Tag'`. */
  typeLabel: string
  /** Player name for an assigned tag; absent otherwise. */
  name?: string
  /** Jersey number for an assigned tag; absent otherwise. */
  number?: number
  /** Device id, always rendered (in parentheses). */
  id: string
}

/**
 * Resolve a device's descriptor pieces — type label, the player name/number for
 * an assigned tag, and the id. Returns null for an out-of-scope device. Both the
 * plain-text `buildMessage` and the JSX toast renderer build from these, so the
 * descriptor's shape (and the player-identity rule: name from `teamPerson`,
 * never `hardwareId`) lives in exactly one place.
 */
export const buildMessageParts = (
  device: FormattedDevice,
  assignmentMap: AssignmentMap
): DeviceMessageParts | null => {
  const typeLabel = getDeviceTypeLabel(device)
  if (!typeLabel) return null

  const assignment = isDeviceType.playerTag(device.type)
    ? assignmentMap.get(device.id)
    : undefined

  return {
    typeLabel,
    name: assignment?.name,
    number: assignment?.number,
    id: device.id
  }
}

/**
 * Build the plain-text toast message for a device/condition. The canonical shape
 * is `{type} {name} #{number} ({id}) — {condition}`, but only the parts that are
 * actually present render — a missing player name or number is dropped rather
 * than printed as `undefined`. Player identity only applies to tags. This is the
 * accessible-label form; the visible toast renders the same parts as styled JSX.
 *
 * - Ball:            `Ball (ball-1) — battery level critical`
 * - Anchor:          `Anchor (anchor-1) — has gone offline`
 * - Assigned tag:    `Tag John Doe #7 (tag-1) — has gone offline`
 * - Assigned tag, no number: `Tag John Doe (tag-1) — has gone offline`
 * - Unassigned / unresolved tag: `Tag (tag-9) — battery level critical`
 * - Come online:     `Tag John Doe #7 (tag-1) — has come online`
 */
export const buildMessage = (
  device: FormattedDevice,
  condition: NotificationCondition,
  assignmentMap: AssignmentMap
) => {
  const parts = buildMessageParts(device, assignmentMap)

  const descriptor = [
    parts?.typeLabel,
    parts?.name || undefined,
    parts?.number != null ? `#${parts.number}` : undefined,
    `(${device.id})`
  ]
    .filter(Boolean)
    .join(' ')

  return `${descriptor} — ${CONDITION_LABELS[condition]}`
}

/**
 * Diff the current snapshot against the previous armed state and produce the
 * notifications to fire plus the next armed state.
 *
 * Battery fires on the not-armed → armed transition: the next armed flag is
 * simply the current critical state, so still-bad keeps it armed (no re-fire)
 * and recovery clears it (re-arming the next entry).
 *
 * Offline is debounced. We count how many consecutive snapshots a device has
 * been offline (`offlineCount`) and only fire once it reaches
 * `OFFLINE_DEBOUNCE_COUNT`. Recovering before then clears the pending state with
 * no toast; recovering after a fire re-arms a fresh episode. Counting snapshots
 * rather than reading a clock keeps this function pure and testable, and makes
 * the debounce track received data rather than elapsed time.
 *
 * A device that is *already* offline the first time we see it (no prior armed
 * entry — e.g. every offline device in the snapshot right after an app reload)
 * is treated as the baseline state, not an event: it is recorded as armed
 * without firing, so reloads don't re-notify for devices that were already
 * offline. Only an offline transition observed within the session fires.
 *
 * Come-online (success) fires in two cases, never at the baseline snapshot:
 *   - a previously-unseen device that joins online mid-session fires immediately;
 *   - a device recovering from a *genuine* offline state (its offline toast had
 *     fired, or it was offline at the baseline) fires once it has been online
 *     for `ONLINE_DEBOUNCE_COUNT` snapshots — the mirror of the offline
 *     debounce. Gating on a genuine offline avoids a spurious "back online" for
 *     a brief packet-loss blip that never raised an offline toast.
 *
 * Battery is suppressed for the first `ONLINE_DEBOUNCE_COUNT` snapshots a device
 * is online (its `0`-on-boot reading isn't trusted yet), then fires on the
 * rising edge.
 *
 * Each call models one diagnostics snapshot; the per-device frame counters live
 * on the armed map and advance one step per call, so no clock is read here.
 */
export const computeTransitions = (
  prevArmed: ArmedStateMap,
  devices: FormattedDevice[],
  assignmentMap: AssignmentMap
) => {
  const toFire: FiredNotification[] = []
  const nextArmed: ArmedStateMap = {}

  // An empty previous state means this is the baseline snapshot (initial load
  // or after total network loss); its devices are the starting point, not
  // arrivals, so they never fire a come-online toast.
  const isBaselineSnapshot = Object.keys(prevArmed).length === 0

  for (const device of devices) {
    if (!isInScope(device)) continue

    const { batteryCritical, offline } = evaluateDevice(device)
    const online = device.metrics.status.value === 'online'
    const prev = prevArmed[device.id]
    const firstSeen = prev === undefined

    // How many consecutive snapshots the device has been online this episode —
    // drives the battery settling window and the come-online recovery debounce.
    // Capped so the counter can't grow unbounded over a long session.
    const onlineCount = online
      ? Math.min((prev?.onlineCount ?? 0) + 1, ONLINE_DEBOUNCE_COUNT)
      : 0
    const settling = online && onlineCount < ONLINE_DEBOUNCE_COUNT

    // Come online — never at the baseline snapshot. Offline devices keep
    // `online: true` so that a pending recovery is the only `online: false`
    // online state, which disambiguates it from a brief unannounced blip.
    let onlineArmed = true
    if (online) {
      if (firstSeen) {
        // A brand-new device joining mid-session announces immediately.
        if (!isBaselineSnapshot) {
          toFire.push({
            id: device.id,
            condition: 'online',
            message: buildMessage(device, 'online', assignmentMap)
          })
        }
      } else if (prev.offline) {
        // Just recovered from a genuine offline — begin the come-online debounce.
        onlineArmed = false
      } else if (!prev.online) {
        // Recovery debounce in progress; fire once online for enough snapshots.
        if (onlineCount >= ONLINE_DEBOUNCE_COUNT) {
          toFire.push({
            id: device.id,
            condition: 'online',
            message: buildMessage(device, 'online', assignmentMap)
          })
        } else {
          onlineArmed = false
        }
      }
    }

    // Battery — fires on the rising edge, but suppressed while settling so a
    // boot-time `0` reading can't trip it before the real value arrives.
    if (batteryCritical && !settling && !prev?.battery) {
      toFire.push({
        id: device.id,
        condition: 'battery',
        message: buildMessage(device, 'battery', assignmentMap)
      })
    }

    // Offline — debounced; only fires after OFFLINE_DEBOUNCE_COUNT consecutive
    // offline snapshots.
    let offlineArmed = false
    let offlineCount = 0
    if (offline) {
      offlineCount = Math.min(
        (prev?.offlineCount ?? 0) + 1,
        OFFLINE_DEBOUNCE_COUNT
      )
      const alreadyFired = prev?.offline ?? false
      const debounceMet = offlineCount >= OFFLINE_DEBOUNCE_COUNT

      if (firstSeen) {
        // Already offline on first observation — baseline state, not an event.
        offlineArmed = true
      } else if (!alreadyFired && debounceMet) {
        toFire.push({
          id: device.id,
          condition: 'offline',
          message: buildMessage(device, 'offline', assignmentMap)
        })
        offlineArmed = true
      } else {
        offlineArmed = alreadyFired
      }
    }

    nextArmed[device.id] = {
      // Latch while still critical: once armed, stay armed for as long as the
      // device remains critical. `!settling` only gates the *initial* arming so
      // a boot-time `0` can't trip it; without `prev?.battery` here, a later
      // settling window (e.g. a still-critical device that sleeps and wakes)
      // would clear the flag and spuriously re-fire after settling. Recovery
      // (batteryCritical === false) still clears it, re-arming the next entry.
      battery: batteryCritical && (prev?.battery || !settling),
      offline: offlineArmed,
      online: onlineArmed,
      offlineCount,
      onlineCount
    }
  }

  return { toFire, nextArmed }
}
